import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Copy, ArrowRight, BadgePercent, Facebook } from "lucide-react";
import { toast } from "sonner";

const PROMO_CODE = "QC2026";

export default function Flyer() {
  const qrValue =
    typeof window !== "undefined"
      ? `${window.location.origin}/#inscription`
      : "https://patdeegan.com/cpmc";

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      toast.success(`Code ${PROMO_CODE} copié !`, {
        description: "Collez-le dans le formulaire d'inscription.",
      });
    } catch {
      toast.info(`Code promo : ${PROMO_CODE}`);
    }
  };

  return (
    <>
    <section id="flyer" className="py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-teal-dark">
            PROMOTION
          </p>
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            Activez <span className="text-outline">le code</span>
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-ink/70 sm:text-lg">
            Scannez le code QR pour accéder directement à l'inscription, ou utilisez
            le code promo <strong className="text-ink">{PROMO_CODE}</strong> lors de votre
            inscription à la première cohorte francophone.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              onClick={copyCode}
              data-testid="cpmc-flyer-copy-promo-btn"
              className="group flex items-center gap-2 rounded-full bg-ink px-6 py-3.5 font-semibold text-cream transition-all duration-300 hover:bg-teal-dark"
            >
              <Copy className="h-4 w-4" />
              Copier le code {PROMO_CODE}
            </button>
          </div>

          <a
            href="#inscription"
            data-testid="cpmc-flyer-cta-register"
            className="group mt-6 inline-flex items-center gap-2 font-semibold text-teal-dark"
          >
            Aller directement à l'inscription
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, rotate: 2, y: 40 }}
          whileInView={{ opacity: 1, rotate: 0, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          whileHover={{ rotate: -1, scale: 1.01 }}
          data-testid="cpmc-flyer-interactive-container"
          className="relative"
        >
          <div className="pointer-events-none absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-teal/30 via-sun/30 to-coral/30 blur-lg" />
          <div className="relative overflow-hidden rounded-[1.75rem] border-2 border-ink bg-cream shadow-2xl">
            <div className="bg-ink px-8 py-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sun">
                Pat Deegan & Associates · 25+ pays
              </p>
              <h3 className="mt-2 font-display text-2xl font-extrabold leading-tight text-cream sm:text-3xl">
                FORMATION CPMC
                <span className="mt-1 block text-base font-bold text-mint sm:text-lg">
                  Certified Personal Medicine Coach — en français
                </span>
              </h3>
            </div>

            <div className="grid gap-6 px-8 py-8 sm:grid-cols-[1fr_auto]">
              <div>
                <p className="font-display text-lg font-bold leading-snug">
                  « On nous a dit que ce n'était pas possible.{" "}
                  <span className="bg-sun/80 px-1">Nous, on vous montre que si</span> »
                </p>
                <p data-testid="cpmc-flyer-quote-attribution" className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50">
                  Les précurseurs
                </p>
                <ul className="mt-5 space-y-2 text-sm text-ink/75">
                  <li>— Début : 1er octobre 2026 · 19 semaines</li>
                  <li>— 100 % en ligne, en français</li>
                  <li>— Certification reconnue à l'international</li>
                  <li>— Animée par des pairs aidants certifiés</li>
                </ul>
                <div
                  data-testid="cpmc-flyer-discount-badge"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-coral px-4 py-2 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white"
                >
                  <BadgePercent className="h-4 w-4" />
                  Code promo : {PROMO_CODE} — 50 % de rabais
                </div>
              </div>

              <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-ink/15 bg-white p-5">
                <div data-testid="cpmc-flyer-qr-code-image" className="rounded-lg border-4 border-ink p-1">
                  <QRCodeSVG value={qrValue} size={132} fgColor="#0A1128" level="M" />
                </div>
                <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
                  Scannez pour
                  <br />
                  vous inscrire
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-ink/10 bg-sun/25 px-8 py-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/70">
                patdeegan.com/cpmc
              </span>
              <a
                href="https://www.facebook.com/patdeegan.pda/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cpmc-flyer-link-facebook"
                className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/70 transition-colors hover:text-teal-dark"
              >
                <Facebook className="h-3.5 w-3.5" />
                /patdeegan.pda
              </a>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/70">
                Première cohorte — 20 places maximum
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>

    <div
      id="cpmc-flyer-print-zone"
      data-testid="cpmc-flyer-print-page"
      className="hidden flex-col bg-cream text-ink"
    >
      <div className="bg-ink px-[0.6in] py-[0.4in]">
        <p className="font-mono text-[8pt] uppercase tracking-[0.3em] text-sun">
          Pat Deegan & Associates — offerte dans plus de 25 pays
        </p>
        <p className="mt-[0.12in] font-display text-[34pt] font-extrabold leading-none text-cream">
          FORMATION CPMC
        </p>
        <p className="mt-[0.08in] font-display text-[13pt] font-bold text-mint">
          Certified Personal Medicine Coach — en français
        </p>
      </div>

      <div className="px-[0.6in] pt-[0.35in]">
        <p className="font-display text-[24pt] font-extrabold leading-[1.15] tracking-tight">
          « On nous a dit que ce n'était pas possible.{" "}
          <span className="bg-sun px-2">Nous, on vous montre que si</span> »
        </p>
        <p className="mt-[0.15in] font-mono text-[8pt] uppercase tracking-[0.25em] text-ink/60">
          Patricia Deegan, PhD × Luc Vigneault — pionniers du rétablissement
        </p>
      </div>

      <div className="flex flex-1 items-start justify-between gap-[0.4in] px-[0.6in] pt-[0.35in]">
        <ul className="max-w-[4.5in] space-y-[0.13in] text-[10.5pt] leading-snug text-ink/85">
          <li><span className="mr-2 inline-block h-2 w-2 rounded-full bg-teal" />Certification CPMC reconnue à l'international dans le réseau de pair-aidance en santé mentale</li>
          <li><span className="mr-2 inline-block h-2 w-2 rounded-full bg-sun" />19 semaines de formation (12 semaines de curriculum original + 7 semaines bonifiées)</li>
          <li><span className="mr-2 inline-block h-2 w-2 rounded-full bg-coral" />Modules de psycho nutrition avec Melanie Plante, CPMC, CPMC-T</li>
          <li><span className="mr-2 inline-block h-2 w-2 rounded-full bg-blaze" />Modules de pharmacologie pour un langage commun avec les équipes cliniques</li>
          <li><span className="mr-2 inline-block h-2 w-2 rounded-full bg-teal" />Accompagnement par les pairs avec Luc Vigneault, premier pair-aidant certifié au Canada</li>
          <li><span className="mr-2 inline-block h-2 w-2 rounded-full bg-sun" />Pour pairs aidants, cliniciens, intervenants psychosociaux, ergothérapeutes, gestionnaires de cas et psychologues</li>
        </ul>

        <div className="flex shrink-0 flex-col items-center gap-[0.1in] rounded-2xl border-2 border-ink bg-white p-[0.18in]">
          <div data-testid="cpmc-flyer-print-qr-code" className="rounded-md border-4 border-ink p-1">
            <QRCodeSVG value={qrValue} size={140} fgColor="#0A1128" level="M" />
          </div>
          <p className="text-center font-mono text-[7.5pt] font-semibold uppercase tracking-[0.2em] text-ink/70">
            Scannez pour<br />vous inscrire
          </p>
        </div>
      </div>

      <div className="mx-[0.6in] mb-[0.3in] mt-[0.3in] flex items-center justify-between rounded-2xl border-2 border-ink bg-sun/70 px-[0.3in] py-[0.18in]">
        <div>
          <p className="font-mono text-[7.5pt] font-semibold uppercase tracking-[0.25em] text-ink/70">
            Code promo — tarif de lancement
          </p>
          <p data-testid="cpmc-flyer-print-promo-code" className="font-display text-[26pt] font-extrabold leading-none">
            {PROMO_CODE}
          </p>
        </div>
        <div className="text-right text-[9.5pt] font-semibold leading-relaxed">
          <p>Début : 1er octobre 2026 — 19 semaines, 3 h par semaine</p>
          <p>100 % en ligne (visioconférence) — en français</p>
          <p>Première cohorte — 20 places maximum</p>
        </div>
      </div>

      <div className="flex items-center justify-between bg-ink px-[0.6in] py-[0.22in]">
        <div className="flex flex-col gap-[0.04in]">
          <span className="font-mono text-[8pt] uppercase tracking-[0.25em] text-cream">
            patdeegan.com/cpmc
          </span>
          <span data-testid="cpmc-flyer-print-facebook" className="font-mono text-[8pt] uppercase tracking-[0.25em] text-mint">
            facebook.com/patdeegan.pda
          </span>
        </div>
        <span className="font-mono text-[8pt] uppercase tracking-[0.25em] text-sun">
          La Médecine Personnelle : ce que nous FAISONS pour aller mieux et le rester
        </span>
      </div>
    </div>
    </>
  );
}
